import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import { useMemo } from 'react'

import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { PARTNER360_CHART_FONT_FAMILY } from 'partner360/configs/typography'

import { IMdfBreakdownGroup } from '../../model/types'

import styles from './MdfBreakdownChart.module.scss'

const PAID_COLOR = '#16a34a'
const REQUESTED_COLOR = '#288bd6'

export const MDF_PAID_COLOR = PAID_COLOR
export const MDF_REQUESTED_COLOR = REQUESTED_COLOR

export const MDF_PAID_SERIES = 'Paid' // t(KEYS.paid)
export const MDF_REQUESTED_SERIES = 'Requested' // t(KEYS.requested)

const PAID_SERIES = MDF_PAID_SERIES
const REQUESTED_SERIES = MDF_REQUESTED_SERIES

/** The chart's own money format — plain units with a currency sign only in USD, unlike the
 *  compact `$1.2M` the tiles use. Exported so anything quoting the chart prints its numbers the
 *  way the chart itself does. */
export const formatMdfChartValue = (value: number, isUsd: boolean) =>
  getFormattedFinance(value, { withCurrencySign: isUsd, maximumFractionDigits: 0 })

interface IMdfBreakdownChartProps {
  /** Already bucketed and sorted — see `lib/getMdfBreakdownGroups`. */
  groups: IMdfBreakdownGroup[]
  /** Whether the amounts are in USD, which is also what decides the currency sign. */
  isUsd: boolean
}

/**
 * Requested vs Paid per bucket, as stacked bars.
 *
 * Presentation only: it takes ready buckets and draws them, so the MDF dashboard (which reads
 * its own store and filters) and the planning cockpit's Initiatives tile (which reads the
 * `planningCockpitGzip` payload) render the same chart from their own data.
 */
export const MdfBreakdownChart = ({ groups, isUsd }: IMdfBreakdownChartProps) => {
  const option = useMemo<EChartsOption>(() => {
    const formatValue = (value: number) => formatMdfChartValue(value, isUsd)

    return {
      textStyle: { fontFamily: PARTNER360_CHART_FONT_FAMILY },

      grid: {
        left: 0,
        right: 10,
        top: 14,
        bottom: 25,
        containLabel: true,
      },

      legend: {
        bottom: 0,
        left: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: {
          color: '#6b7280',
          fontSize: 14,
        },
      },

      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        /**
         * The requested series plots only the unpaid remainder (see below), so the
         * tooltip cannot report plotted values — it reads the real requested and
         * paid amounts off the group instead.
         */
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params]
          const group = groups[items[0]?.dataIndex]

          if (!group) {
            return ''
          }

          const rows = items.map((item: any) => {
            const value = item.seriesName === PAID_SERIES ? group.paid : group.requested

            return `<div>${item.marker}${item.seriesName}<span style="float:right;margin-left:20px;font-weight:600">${formatValue(value)}</span></div>`
          })

          return `<div style="font-weight:600;margin-bottom:4px">${group.name}</div>${rows.join('')}`
        },
      },

      xAxis: {
        type: 'category',
        data: groups.map((group) => group.name),
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: '#666',
          fontSize: 14,
          rotate: 25,
          interval: 0,
          formatter: (value: string) => (value.length > 10 ? `${value.slice(0, 10)}...` : value),
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#d9d9d9',
          },
        },
      },

      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#666',
          fontSize: 14,
          formatter: (value: number) => formatValue(value),
        },
        splitLine: {
          lineStyle: {
            color: '#d9d9d9',
          },
        },
      },

      series: [
        {
          name: PAID_SERIES,
          type: 'bar',
          stack: 'breakdown',
          barWidth: '72%',
          data: groups.map((group) => group.paid),
          itemStyle: {
            color: PAID_COLOR,
          },
        },
        {
          /**
           * Paid is part of requested, not money on top of it, so stacking both in
           * full would make the bar `requested + paid` tall. Only the unpaid
           * remainder is stacked above the paid segment, which puts the bar at
           * `max(requested, paid)`: the green part reads as the paid share of the
           * blue whole. Tooltip values come from the group, not from here.
           */
          name: REQUESTED_SERIES,
          type: 'bar',
          stack: 'breakdown',
          data: groups.map((group) => Math.max(group.requested - group.paid, 0)),
          itemStyle: {
            color: REQUESTED_COLOR,
          },
        },
      ],
    }
  }, [groups, isUsd])

  return (
    <div className={styles.root}>
      <ReactEcharts option={option} opts={{ renderer: 'svg' }} />
    </div>
  )
}

MdfBreakdownChart.displayName = 'MdfBreakdownChart'
